/**
 * JurisdictionsPanel
 *
 * SYSTEM_ADMIN panel for managing the jurisdiction hierarchy.
 * Displays an expandable tree with artifact counts per jurisdiction.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, Pencil, Loader2, ChevronRight, Search } from 'lucide-react';
import { useJurisdictions } from '@/hooks/useJurisdictions';
import { apiFetch } from '@/lib/apiFetch';
import type { Jurisdiction } from '@/types/database';

const LEVELS: Jurisdiction['level'][] = ['state', 'county', 'place', 'body'];

const LEVEL_COLORS: Record<string, string> = {
  state: 'bg-blue-100 text-blue-700',
  county: 'bg-amber-100 text-amber-700',
  place: 'bg-rose-100 text-rose-700',
  body: 'bg-slate-100 text-slate-700',
};

const LEVEL_ICONS: Record<string, string> = {
  state: '🏛',
  county: '🏢',
  place: '📍',
  body: '⚖',
};

interface JurFormData {
  name: string;
  level: string;
  parent_id: string;
  path: string;
}

const EMPTY: JurFormData = {
  name: '',
  level: 'place',
  parent_id: '',
  path: '',
};

// ── JurDialog (unchanged from original) ──────────────────────────────────────

function JurDialog({
  initial,
  flat,
  onSave,
  trigger,
  title,
}: {
  initial?: Jurisdiction;
  flat: Jurisdiction[];
  onSave: (data: JurFormData) => Promise<void>;
  trigger: React.ReactNode;
  title: string;
}) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<JurFormData>(
    initial
      ? {
          name: initial.name,
          level: initial.level,
          parent_id: initial.parent_id ?? '',
          path: initial.path ?? '',
        }
      : EMPTY
  );
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function handleOpen(next: boolean) {
    if (next) {
      setForm(
        initial
          ? {
              name: initial.name,
              level: initial.level,
              parent_id: initial.parent_id ?? '',
              path: initial.path ?? '',
            }
          : EMPTY
      );
      setErr(null);
    }
    setOpen(next);
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setErr('Name is required.');
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      await onSave(form);
      setOpen(false);
    } catch (ex: unknown) {
      setErr(ex instanceof Error ? ex.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-2">
          <div className="space-y-1">
            <Label htmlFor="jur-name">Name *</Label>
            <Input
              id="jur-name"
              placeholder="e.g. Riverside County"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Level</Label>
              <Select
                value={form.level}
                onValueChange={(v) => setForm({ ...form, level: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LEVELS.map((l) => (
                    <SelectItem key={l} value={l}>
                      {l.charAt(0).toUpperCase() + l.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Parent</Label>
              <Select
                value={form.parent_id || '__none__'}
                onValueChange={(v) =>
                  setForm({ ...form, parent_id: v === '__none__' ? '' : v })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="None (top level)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None (top level)</SelectItem>
                  {flat
                    .filter((j) => !initial || j.id !== initial.id)
                    .map((j) => (
                      <SelectItem key={j.id} value={j.id}>
                        {j.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="jur-path">
              ltree path{' '}
              <span className="text-gray-400 font-normal">(optional)</span>
            </Label>
            <Input
              id="jur-path"
              placeholder="e.g. CA.Riverside"
              value={form.path}
              onChange={(e) => setForm({ ...form, path: e.target.value })}
            />
            <p className="text-xs text-gray-500">
              Dot-separated ltree path used for ancestor queries. Leave blank to
              auto-generate.
            </p>
          </div>
          {err && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
              {err}
            </p>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Tree node ─────────────────────────────────────────────────────────────────

function JurNode({
  node,
  depth,
  counts,
  expanded,
  onToggle,
  flat,
  canEdit,
  createJurisdiction,
  updateJurisdiction,
}: {
  node: Jurisdiction;
  depth: number;
  counts: Map<string, number>;
  expanded: Set<string>;
  onToggle: (id: string) => void;
  flat: Jurisdiction[];
  canEdit: boolean;
  createJurisdiction: (
    data: Omit<Jurisdiction, 'id' | 'created_at' | 'updated_at'>
  ) => Promise<Jurisdiction>;
  updateJurisdiction: (
    id: string,
    data: Partial<Jurisdiction>
  ) => Promise<Jurisdiction>;
}) {
  const children = (node.children as Jurisdiction[] | undefined) ?? [];
  const hasChildren = children.length > 0;
  const isOpen = expanded.has(node.id);
  const count = counts.get(node.id) ?? 0;
  const padLeft = depth * 24;

  return (
    <>
      <div
        className="flex items-center gap-2 border-b border-gray-50 py-2 px-4 hover:bg-gray-50 transition-colors"
        style={{ paddingLeft: 16 + padLeft }}
      >
        {/* Expand/collapse */}
        <button
          type="button"
          onClick={() => hasChildren && onToggle(node.id)}
          className={`shrink-0 rounded p-0.5 text-gray-400 hover:text-gray-600 transition-transform ${
            hasChildren ? 'visible' : 'invisible'
          }`}
        >
          <ChevronRight
            className={`h-3.5 w-3.5 transition-transform ${isOpen ? 'rotate-90' : ''}`}
          />
        </button>

        {/* Icon + Name */}
        <div className="flex-1 min-w-0 flex items-center gap-1.5">
          <span className="text-sm shrink-0">
            {(LEVEL_ICONS as Record<string, string>)[node.level] ?? '📍'}
          </span>
          <span className="text-sm font-medium text-gray-900 truncate">
            {node.name}
          </span>
        </div>

        {/* Level */}
        <Badge
          className={`text-[10px] shrink-0 ${LEVEL_COLORS[node.level] ?? 'bg-gray-100 text-gray-600'}`}
        >
          {node.level}
        </Badge>

        {/* Artifact count */}
        {count > 0 && (
          <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-500 min-w-[32px] text-center">
            {count.toLocaleString()}
          </span>
        )}

        {/* Path */}
        <span className="shrink-0 font-mono text-[10px] text-gray-400 w-24 truncate text-right">
          {node.path ?? '—'}
        </span>

        {/* ID */}
        <span className="shrink-0 font-mono text-[10px] text-gray-300 w-20 truncate text-right">
          {node.id.slice(0, 8)}
        </span>

        {/* Edit */}
        {canEdit && (
          <JurDialog
            initial={node}
            flat={flat}
            title="Edit Jurisdiction"
            onSave={async (data) => {
              await updateJurisdiction(node.id, {
                name: data.name,
                level: data.level as Jurisdiction['level'],
                parent_id: data.parent_id || null,
                path: data.path || null,
              });
            }}
            trigger={
              <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0">
                <Pencil className="w-3 h-3" />
              </Button>
            }
          />
        )}
      </div>

      {/* Children */}
      {isOpen &&
        hasChildren &&
        children.map((child) => (
          <JurNode
            key={child.id}
            node={child}
            depth={depth + 1}
            counts={counts}
            expanded={expanded}
            onToggle={onToggle}
            flat={flat}
            canEdit={canEdit}
            createJurisdiction={createJurisdiction}
            updateJurisdiction={updateJurisdiction}
          />
        ))}
    </>
  );
}

// ── Main panel ────────────────────────────────────────────────────────────────

export const JurisdictionsPanel: React.FC<{ canEdit?: boolean }> = ({
  canEdit = true,
}) => {
  const { tree, flat, loading, error, createJurisdiction, updateJurisdiction } =
    useJurisdictions();

  const [counts, setCounts] = useState<Map<string, number>>(new Map());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');

  // Fetch artifact counts
  useEffect(() => {
    let cancelled = false;
    apiFetch('/api/jurisdictions/artifact-counts')
      .then((r) => r.json())
      .then((json) => {
        if (cancelled) return;
        const m = new Map<string, number>();
        const rows: { jurisdiction_id: string; count: number }[] =
          Array.isArray(json) ? json : (json?.data ?? []);
        for (const row of rows) m.set(row.jurisdiction_id, row.count);
        setCounts(m);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Toggle expand
  const handleToggle = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Expand/collapse all
  const expandAll = () => {
    const all = new Set<string>();
    function collect(nodes: Jurisdiction[]) {
      for (const n of nodes) {
        const kids = n.children as Jurisdiction[] | undefined;
        if (kids?.length) {
          all.add(n.id);
          collect(kids);
        }
      }
    }
    collect(tree);
    setExpanded(all);
  };
  const collapseAll = () => setExpanded(new Set());

  // Filter tree by search — only activates when searching
  const displayTree = useMemo(() => {
    if (!search.trim()) return tree;
    const q = search.toLowerCase();
    const matches = flat.filter(
      (j) =>
        j.name.toLowerCase().includes(q) ||
        (j.path ?? '').toLowerCase().includes(q)
    );
    // Show matching nodes with their full path context (ancestors visible)
    const matchSet = new Set(matches.map((m) => m.id));
    // Add ancestors of matching nodes
    const ancestorIds = new Set<string>();
    const idToNode = new Map(flat.map((j) => [j.id, j]));
    for (const m of matches) {
      let current: Jurisdiction | undefined = m;
      while (current?.parent_id) {
        ancestorIds.add(current.parent_id);
        current = idToNode.get(current.parent_id);
      }
    }
    // Show only matching nodes + their ancestors
    function show(nodes: Jurisdiction[]): Jurisdiction[] {
      return nodes
        .filter((n) => matchSet.has(n.id) || ancestorIds.has(n.id))
        .map((n) => ({
          ...n,
          children: n.children ? show(n.children) : [],
        }));
    }
    return show(tree);
  }, [tree, search, flat]);

  // Virtualize: only render nodes visible in the expanded hierarchy
  const visibleNodes = useMemo(() => {
    const result: Array<{ node: Jurisdiction; depth: number }> = [];
    function walk(nodes: Jurisdiction[], depth: number) {
      for (const n of nodes) {
        result.push({ node: n, depth });
        const kids = n.children as Jurisdiction[] | undefined;
        if (kids?.length && expanded.has(n.id)) {
          walk(kids, depth + 1);
        }
      }
    }
    walk(displayTree, 0);
    return result;
  }, [displayTree, expanded]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-400">
        <Loader2 className="w-6 h-6 animate-spin mr-2" />
        Loading jurisdictions…
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-600 bg-red-50 border border-red-200 rounded p-4">
        {error}
      </div>
    );
  }

  if (tree.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        No jurisdictions yet.
      </div>
    );
  }

  return (
    <Card>
      <CardContent className="p-0">
        {/* Toolbar */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Search jurisdictions…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 h-8 text-sm"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={expandAll}
            className="text-xs h-8"
          >
            Expand All
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={collapseAll}
            className="text-xs h-8"
          >
            Collapse
          </Button>
          {canEdit && (
            <JurDialog
              flat={flat}
              title="Add Jurisdiction"
              onSave={async (data) => {
                await createJurisdiction({
                  name: data.name,
                  level: data.level,
                  parent_id: data.parent_id || null,
                  path: data.path || null,
                } as Omit<Jurisdiction, 'id' | 'created_at' | 'updated_at'>);
              }}
              trigger={
                <Button size="sm" className="h-8 text-xs">
                  <Plus className="w-3.5 h-3.5 mr-1.5" />
                  Add
                </Button>
              }
            />
          )}
        </div>

        {/* Column headers */}
        <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-4 py-1.5 text-[10px] font-semibold uppercase text-gray-400">
          <span className="w-5 shrink-0" />
          <span className="flex-1">Name</span>
          <span className="w-16 shrink-0 text-center">Level</span>
          <span className="w-12 shrink-0 text-center">Docs</span>
          <span className="w-24 shrink-0 text-right">Path</span>
          <span className="w-20 shrink-0 text-right">ID</span>
          {canEdit && <span className="w-7 shrink-0" />}
        </div>

        {/* Tree — virtualized, only renders expanded nodes */}
        <div>
          {visibleNodes.map(({ node, depth }) => (
            <JurNode
              key={node.id}
              node={node}
              depth={depth}
              counts={counts}
              expanded={expanded}
              onToggle={handleToggle}
              flat={flat}
              canEdit={canEdit}
              createJurisdiction={createJurisdiction}
              updateJurisdiction={updateJurisdiction}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
